// Staff vendor-bill record + AP action surface (handoff W16). Hits
// GET /bills/:id which returns the bill row enriched with vendor name + PO/line
// attribution plus the bill's line items (each carrying the PO snapshot it was
// matched against and the server-computed variance).
//
// Finance/admin staff move a bill through its lifecycle (submit for review →
// approve → mark paid, with dispute / void off-ramps), all wired to
// POST /bills/:id/transition. The state machine lives in services/bills.ts:
//   draft → pending_review → approved → paid
//   any pre-paid status → disputed → approved | voided
// Dispute + void require a reason (captured in an inline form). Mutations are
// gated on admin / finance (matching the route roles). Linked from
// ProjectBillsTab + BillingPage (same-tab navigation).
//
// Layout (other Business UI handoff, 2026-10-08): 32px record header + status +
// facts, ONE dominant action (Approve when available, else Mark paid), the
// rest as outline buttons. Approval and payment are separate steps; a line that
// differs from its PO is flagged per line and in the header. Duplicate vendor
// bill numbers can't exist — the database refuses them at entry.
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Archive, ArrowLeft, Ban, Check, DollarSign, Send } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { Fact, FactsRow, TABLE_HEAD, TD, TH, TROW } from "@/components/ui/RecordParts";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";
import { NoteText } from "@/components/notes/MentionTextarea";
import { SupplierBillFile } from "@/components/finance/SupplierBillFile";

interface BillDetail {
  id: string;
  purchaseOrderId: string | null;
  lineItemId: string | null;
  vendorId: string;
  vendorName: string;
  poNumber: number | null;
  lineNumber: number | null;
  lineServiceType: string | null;
  vendorBillNumber: string | null;
  billDate: string;
  dueDate: string | null;
  subtotal: string;
  tax: string;
  shippingCost: string;
  total: string;
  status: string;
  ingestionSource: string;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  approvedAt: string | null;
  paidAt: string | null;
  /** 'not_applicable' = closed by hand, never sent to QuickBooks. */
  qboSyncStatus?: string | null;
  /** The supplier's bill, if attached (Stephanie 2026-10-22). */
  file?: { fileId: string; filename: string; url: string | null } | null;
}

type VarianceReason =
  | "pricing_drift"
  | "vendor_surcharge"
  | "partial_shipment"
  | "vendor_waived"
  | "qty_change"
  | "freight_estimate"
  | "other";

interface BillLine {
  id: string;
  billId: string;
  description: string;
  chargeType: string;
  quantityShipped: string | null;
  unitCost: string;
  total: string | null;
  /** What the PO said when this line was matched (null = not matched to a PO line). */
  poQuantitySnapshot?: string | null;
  poUnitCostSnapshot?: string | null;
  /** Server-generated: billed − PO snapshot. Only meaningful when the matching
   *  snapshot is present (the column treats a missing snapshot as 0). */
  varianceQty?: string | null;
  varianceCost?: string | null;
  varianceReason?: VarianceReason | null;
}

export interface BillDetailResponse {
  bill: BillDetail;
  lines: BillLine[];
}

function statusTone(
  s: string,
): "active" | "prospect" | "dormant" | "closed" | "warn" | "info" {
  switch (s) {
    case "draft":
    case "pending_review":
      return "warn";
    case "approved":
      return "info";
    case "paid":
      return "active";
    case "disputed":
      return "warn";
    case "voided":
      return "closed";
    default:
      return "info";
  }
}

function statusLabel(s: string): string {
  switch (s) {
    case "draft":
      return "Draft";
    case "pending_review":
      return "To review";
    case "approved":
      return "Approved · not paid";
    case "disputed":
      return "Disputed";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
    default:
      return s.replace(/_/g, " ");
  }
}

function reasonLabel(r: VarianceReason | null | undefined): string | null {
  switch (r) {
    case "pricing_drift":
      return "Pricing changed";
    case "vendor_surcharge":
      return "Vendor surcharge";
    case "partial_shipment":
      return "Partial shipment";
    case "vendor_waived":
      return "Vendor waived";
    case "qty_change":
      return "Quantity change";
    case "freight_estimate":
      return "Freight estimate";
    case "other":
      return "Other";
    default:
      return null;
  }
}

function formatMoney(s: string | null): string {
  if (s === null) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

/** Unit costs carry up to 4 decimals (cost column scale). */
function formatUnit(s: string | null | undefined): string {
  if (s === null || s === undefined) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function formatQty(s: string | null | undefined): string {
  if (s === null || s === undefined) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function signed(text: string, n: number): string {
  return n > 0 ? `+${text}` : n < 0 ? `−${text.replace(/^-/, "")}` : text;
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function attributionLabel(bill: BillDetail): string {
  if (bill.poNumber !== null) return `PO #${bill.poNumber}`;
  if (bill.lineNumber !== null) {
    const svc =
      bill.lineServiceType && bill.lineServiceType !== "product"
        ? ` · ${bill.lineServiceType.replace(/_/g, " ")}`
        : "";
    return `Line #${bill.lineNumber}${svc}`;
  }
  return "Not linked";
}

/** A line's variance against its PO snapshot — read from the server's
 *  generated columns, only where a snapshot exists. */
function lineVariance(l: BillLine): { qty: number | null; cost: number | null } {
  const q =
    l.poQuantitySnapshot !== null && l.poQuantitySnapshot !== undefined
      ? Number(l.varianceQty ?? 0)
      : null;
  const c =
    l.poUnitCostSnapshot !== null && l.poUnitCostSnapshot !== undefined
      ? Number(l.varianceCost ?? 0)
      : null;
  return {
    qty: q !== null && Number.isFinite(q) && q !== 0 ? q : null,
    cost: c !== null && Number.isFinite(c) && c !== 0 ? c : null,
  };
}

const FREIGHT_CHARGES = new Set(["shipping", "fuel_surcharge", "drop_ship", "handling"]);

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string } | undefined;
    return body?.error ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

type BillEvent =
  | "submit_for_review"
  | "approve"
  | "dispute"
  | "mark_paid"
  | "close"
  | "void";

function okMsg(event: BillEvent): string {
  switch (event) {
    case "submit_for_review":
      return "Bill submitted for review.";
    case "approve":
      return "Bill approved. It isn't paid yet — mark it paid once the payment has gone out.";
    case "dispute":
      return "Bill disputed. The reason is saved on the bill.";
    case "mark_paid":
      return "Bill marked paid.";
    case "close":
      return "Bill closed. It's off the open bills and won't be sent to QuickBooks.";
    case "void":
      return "Bill voided. The reason is saved on the bill.";
  }
}

export function BillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage =
    user?.roles.some((r) => r === "admin" || r === "finance") ?? false;

  const detailKey = ["bills", id, "detail"];
  // Attach / replace / remove the supplier's bill (Stephanie 2026-10-22).
  const saveFile = useMutation({
    mutationFn: (fileId: string | null) => api(`/bills/${id}/file`, { method: "PUT", body: JSON.stringify({ file_id: fileId }) }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: detailKey }); },
  });
  const { data, isLoading, isError } = useQuery({
    queryKey: detailKey,
    queryFn: () => api<BillDetailResponse>(`/bills/${id}`),
    enabled: Boolean(id),
  });

  const [banner, setBanner] = useState<{ kind: "ok" | "err"; msg: string } | null>(
    null,
  );
  // Dispute + void both require a reason — clicking either reveals an
  // inline form rather than firing immediately.
  const [reasonMode, setReasonMode] = useState<null | "dispute" | "void" | "close">(null);
  const [reasonText, setReasonText] = useState("");

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: detailKey });

  const transition = useMutation({
    mutationFn: (body: {
      event: BillEvent;
      dispute_reason?: string;
      void_reason?: string;
      close_reason?: string;
    }) =>
      api(`/bills/${id}/transition`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (_res, vars) => {
      setBanner({ kind: "ok", msg: okMsg(vars.event) });
      setReasonMode(null);
      setReasonText("");
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ["finance"] });
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  if (isLoading) {
    return (
      <div className="mx-auto w-full" aria-busy="true">
        <div className="mt-2 h-3 w-28 animate-pulse rounded bg-canvas" />
        <div className="mt-3 h-9 w-56 animate-pulse rounded bg-canvas" />
        <div className="mt-6 h-40 w-full animate-pulse rounded-md bg-canvas" />
        <p className="sr-only">Loading bill…</p>
      </div>
    );
  }

  if (isError || !data?.bill) {
    return (
      <div className="mx-auto w-full">
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-5 text-sm text-risk">
          We couldn't load this bill. Nothing has changed — refresh to try again.
        </div>
        <Link
          to="/finance/billing"
          className="mt-4 inline-flex min-h-[40px] items-center gap-1 text-sm text-ink hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to billing
        </Link>
      </div>
    );
  }

  const bill = data.bill;
  const lines = data.lines;
  const status = bill.status;

  // Available transitions per the bills.ts state machine.
  const canSubmit = status === "draft";
  const canApprove = ["draft", "pending_review", "disputed"].includes(status);
  const canDispute = ["draft", "pending_review", "approved"].includes(status);
  const canMarkPaid = status === "approved";
  const canVoid = ["draft", "pending_review", "approved", "disputed"].includes(
    status,
  );
  // Close by hand: settled outside the normal flow (e.g. shipping companies),
  // never sent to QuickBooks (Stephanie 2026-10-13).
  const canClose = canVoid;
  const closedNotExported = status === "paid" && bill.qboSyncStatus === "not_applicable";
  const hasActions =
    canManage &&
    (canSubmit || canApprove || canDispute || canMarkPaid || canVoid || canClose);
  // ONE dominant action: Approve when it's available, else Mark paid.
  const primary: "approve" | "pay" | null = canApprove ? "approve" : canMarkPaid ? "pay" : null;

  // Variance vs the PO, from the server's per-line figures.
  const varianceLines = lines.filter((l) => {
    const v = lineVariance(l);
    return v.qty !== null || v.cost !== null;
  });
  const hasVariance = varianceLines.length > 0;
  const qtyVarianceCount = varianceLines.filter((l) => lineVariance(l).qty !== null).length;
  const freightVariance = varianceLines.some((l) => FREIGHT_CHARGES.has(l.chargeType));

  function fire(event: BillEvent) {
    setBanner(null);
    transition.mutate({ event });
  }

  function confirmReason() {
    if (reasonMode === null) return;
    const reason = reasonText.trim();
    if (reason.length === 0) return;
    transition.mutate(
      reasonMode === "dispute"
        ? { event: "dispute", dispute_reason: reason }
        : reasonMode === "close"
          ? { event: "close", close_reason: reason }
          : { event: "void", void_reason: reason },
    );
  }

  return (
    <div className="mx-auto w-full">
      {/* Back link — bills can originate from multiple projects, so there's
          no single "back to project" target. The AP queue at
          /finance/billing is the canonical bill list. */}
      <Link
        to="/finance/billing"
        className="mb-2 inline-flex min-h-[32px] items-center gap-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted hover:text-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Billing
      </Link>

      {/* Record header */}
      <header className="mb-6 flex flex-wrap items-start justify-between gap-x-8 gap-y-5">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Vendor bill
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
              {bill.vendorBillNumber ? `Bill ${bill.vendorBillNumber}` : "Bill (no number)"}
            </h1>
            <StatusPill className="normal-case" tone={statusTone(bill.status)}>{closedNotExported ? "Closed" : statusLabel(bill.status)}</StatusPill>
            {hasVariance && <StatusPill className="normal-case" tone="warn">Differs from PO</StatusPill>}
          </div>
          <FactsRow>
            <Fact label="Vendor">{bill.vendorName}</Fact>
            <Fact label="Purchase order">
              {bill.purchaseOrderId && bill.poNumber !== null ? (
                <Link to={`/pos/${bill.purchaseOrderId}`} className="underline-offset-4 hover:underline">
                  PO #{bill.poNumber}
                </Link>
              ) : (
                attributionLabel(bill)
              )}
            </Fact>
            <Fact label="Bill date">{formatDate(bill.billDate)}</Fact>
            <Fact label="Due">{formatDate(bill.dueDate)}</Fact>
            <Fact label="Approved">{bill.approvedAt ? formatDate(bill.approvedAt) : "Not yet"}</Fact>
            <Fact label="Paid">{bill.paidAt ? formatDate(bill.paidAt) : "Not yet"}</Fact>
          </FactsRow>
        </div>
        <div className="w-full rounded-md border border-rule bg-white px-5 py-4 sm:w-auto sm:min-w-[220px] sm:text-right">
          <p className="text-xs text-muted">Bill total</p>
          <p
            className={cn(
              "mt-1 text-[32px] font-semibold leading-tight tracking-[-1px] tabular-nums",
              status === "voided" ? "text-muted line-through" : "text-ink",
            )}
          >
            {formatMoney(bill.total)}
          </p>
          <p className="mt-1 text-xs text-muted">
            {closedNotExported
              ? "Closed — not sent to QuickBooks"
              : status === "paid"
              ? "Paid"
              : status === "approved"
                ? "Approved — not paid yet"
                : status === "voided"
                  ? "Voided — nothing owed"
                  : "Not approved yet"}
          </p>
        </div>
      </header>

      {/* Actions */}
      {hasActions && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {canApprove && (
            <Button
              type="button"
              variant={primary === "approve" ? "primary" : "outline"}
              onClick={() => fire("approve")}
              disabled={transition.isPending}
            >
              <Check className="h-4 w-4" /> Approve
            </Button>
          )}
          {canMarkPaid && (
            <Button
              type="button"
              variant={primary === "pay" ? "primary" : "outline"}
              onClick={() => fire("mark_paid")}
              disabled={transition.isPending}
              title="Records that the payment has gone out. It doesn't send money."
            >
              <DollarSign className="h-4 w-4" /> Mark paid
            </Button>
          )}
          {canSubmit && (
            <Button
              type="button"
              variant="outline"
              onClick={() => fire("submit_for_review")}
              disabled={transition.isPending}
            >
              <Send className="h-4 w-4" /> Submit for review
            </Button>
          )}
          {canDispute && (
            <Button
              type="button"
              variant="outline"
              aria-expanded={reasonMode === "dispute"}
              onClick={() => {
                setReasonMode((m) => (m === "dispute" ? null : "dispute"));
                setReasonText("");
                setBanner(null);
              }}
            >
              <AlertTriangle className="h-4 w-4" /> Dispute
            </Button>
          )}
          {canClose && (
            <Button
              type="button"
              variant="outline"
              aria-expanded={reasonMode === "close"}
              title="Settled outside the normal flow (e.g. a shipping company). Takes it off the open bills and it's never sent to QuickBooks."
              onClick={() => {
                setReasonMode((m) => (m === "close" ? null : "close"));
                setReasonText("");
                setBanner(null);
              }}
            >
              <Archive className="h-4 w-4" /> Close (don&apos;t export)
            </Button>
          )}
          {canVoid && (
            <Button
              type="button"
              variant="outline"
              className="text-risk"
              aria-expanded={reasonMode === "void"}
              onClick={() => {
                setReasonMode((m) => (m === "void" ? null : "void"));
                setReasonText("");
                setBanner(null);
              }}
            >
              <Ban className="h-4 w-4" /> Void
            </Button>
          )}
        </div>
      )}
      {canMarkPaid && canManage && (
        <p className="-mt-3 mb-6 text-xs text-muted">
          Approved bills are paid from the bank as usual. Mark paid records that it went out — it doesn&apos;t send money.
        </p>
      )}

      {/* Banner */}
      {banner && (
        <div
          role={banner.kind === "err" ? "alert" : "status"}
          className={cn(
            "mb-6 rounded-md border px-4 py-3 text-sm",
            banner.kind === "ok"
              ? "border-ok/30 bg-ok-bg text-ok"
              : "border-risk/30 bg-risk-bg text-risk",
          )}
        >
          {banner.kind === "err" && <span className="font-medium">Nothing changed. </span>}
          {banner.msg}
        </div>
      )}

      {/* Dispute / void reason form */}
      {reasonMode !== null && canManage && (
        <div
          className={cn(
            "mb-6 rounded-md border bg-white p-[22px]",
            reasonMode === "void" ? "border-risk/30" : "border-warn/30",
          )}
        >
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
            {reasonMode === "dispute" ? "Dispute this bill" : reasonMode === "close" ? "Close this bill" : "Void this bill"}
          </h2>
          <p className="mt-1 text-sm text-muted">
            {reasonMode === "dispute"
              ? "The bill stops here until it's resolved. The reason is kept on the bill."
              : reasonMode === "close"
                ? "For bills settled outside the normal flow (like shipping companies). It's marked paid, comes off the open bills, still counts as a cost on the job — and is never sent to QuickBooks. The reason is kept on the bill."
                : "Voiding cancels the bill — nothing will be owed on it. The reason is kept on the bill."}
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="min-w-[220px] flex-1 text-xs text-muted">
              Reason (required)
              <input
                type="text"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder={
                  reasonMode === "dispute"
                    ? "What's wrong with this bill?"
                    : reasonMode === "close"
                      ? "e.g. Paid on the UPS account — not entered in QuickBooks"
                      : "Why is this bill being voided?"
                }
                className="mt-1 block h-10 w-full rounded border border-rule bg-white px-3 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]"
              />
            </label>
            <Button
              type="button"
              variant={reasonMode === "void" ? "risk" : "primary"}
              disabled={transition.isPending || reasonText.trim().length === 0}
              onClick={confirmReason}
            >
              {transition.isPending
                ? "Saving…"
                : reasonMode === "dispute"
                  ? "Confirm dispute"
                  : reasonMode === "close"
                    ? "Close bill"
                    : "Confirm void"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setReasonMode(null);
                setReasonText("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Variance flag — check before approving. */}
      {hasVariance && (
        <div role="note" className="mb-6 flex gap-3 rounded-md border border-warn/30 bg-warn-bg px-4 py-3 text-sm text-ink">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" aria-hidden />
          <p>
            <span className="font-medium text-warn">
              {varianceLines.length} {varianceLines.length === 1 ? "line differs" : "lines differ"} from the PO.
            </span>{" "}
            {qtyVarianceCount > 0 &&
              `${qtyVarianceCount} ${qtyVarianceCount === 1 ? "has" : "have"} a quantity difference. `}
            {freightVariance && "Freight was billed differently from the PO. "}
            Check the lines below{canApprove ? " before approving" : ""}.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(260px,1fr)]">
        <section className="min-w-0 overflow-hidden rounded-md border border-rule bg-white">
          <div className="px-[22px] pb-3 pt-[22px]">
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Lines</h2>
            <p className="mt-1 text-xs text-muted">Billed against what the PO said when the bill was matched.</p>
          </div>
          {lines.length === 0 ? (
            <p className="border-t border-rule px-[22px] py-6 text-sm text-muted">
              No line items on this bill — it was entered as totals only.
            </p>
          ) : (
            <div className="overflow-x-auto border-t border-rule" role="region" aria-label="Bill lines" tabIndex={0}>
              <table className="w-full text-left">
                <thead className={TABLE_HEAD}>
                  <tr>
                    <th scope="col" className={TH}>Line</th>
                    <th scope="col" className={`${TH} text-right`}>Qty billed</th>
                    <th scope="col" className={`${TH} hidden text-right sm:table-cell`}>Unit cost</th>
                    <th scope="col" className={`${TH} text-right`}>Total</th>
                  </tr>
                </thead>
                <tbody className="text-[13px]">
                  {lines.map((l) => {
                    const v = lineVariance(l);
                    const reason = reasonLabel(l.varianceReason);
                    return (
                      <tr key={l.id} className={TROW}>
                        <td className={TD}>
                          <span className="block text-ink">{l.description}</span>
                          <span className="block text-[11px] capitalize text-muted">
                            {l.chargeType.replace(/_/g, " ")}
                          </span>
                          {(v.qty !== null || v.cost !== null) && (
                            <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              <StatusPill className="normal-case" tone="warn">
                                {[
                                  v.qty !== null ? `Qty ${signed(formatQty(l.varianceQty), v.qty)}` : null,
                                  v.cost !== null ? `Cost ${signed(formatUnit(l.varianceCost), v.cost)}/unit` : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </StatusPill>
                              <span className="text-[11px] text-muted">
                                {reason ? `Reason: ${reason}` : "No reason given"}
                              </span>
                            </span>
                          )}
                        </td>
                        <td className={`${TD} text-right tabular-nums`}>
                          <span className="text-ink">{formatQty(l.quantityShipped)}</span>
                          {l.poQuantitySnapshot != null && (
                            <span className="block text-[11px] text-muted">PO {formatQty(l.poQuantitySnapshot)}</span>
                          )}
                        </td>
                        <td className={`${TD} hidden text-right tabular-nums sm:table-cell`}>
                          <span className="text-ink">{formatUnit(l.unitCost)}</span>
                          {l.poUnitCostSnapshot != null && (
                            <span className="block text-[11px] text-muted">PO {formatUnit(l.poUnitCostSnapshot)}</span>
                          )}
                        </td>
                        <td className={`${TD} text-right font-medium tabular-nums text-ink`}>
                          {formatMoney(l.total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="space-y-6">
          <section className="rounded-md border border-rule bg-white p-[22px]">
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Amounts</h2>
            <dl className="mt-3 divide-y divide-[#e7e7e4] text-sm">
              <TotalsRow label="Subtotal" value={formatMoney(bill.subtotal)} />
              <TotalsRow label="Tax" value={formatMoney(bill.tax)} />
              <TotalsRow label="Freight" value={formatMoney(bill.shippingCost)} />
              <TotalsRow label="Bill total" value={formatMoney(bill.total)} emphasis />
            </dl>
          </section>
          <section className="rounded-md border border-rule bg-white p-[22px]">
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Record</h2>
            <dl className="mt-3 space-y-3 text-sm">
              <DetailRow label="Vendor bill #">{bill.vendorBillNumber ?? "None"}</DetailRow>
              <DetailRow label="Entered">
                {formatDate(bill.createdAt)}
                {bill.ingestionSource === "manual" ? " · by hand" : ` · ${bill.ingestionSource.replace(/_/g, " ")}`}
              </DetailRow>
              <DetailRow label="Approved">{bill.approvedAt ? formatDate(bill.approvedAt) : "Not yet"}</DetailRow>
              <DetailRow label="Paid">{bill.paidAt ? formatDate(bill.paidAt) : "Not yet"}</DetailRow>
            </dl>
            <p className="mt-3 text-xs text-muted">
              A vendor can&apos;t have two bills with the same number — duplicates are refused when a bill is entered.
            </p>
            <div className="mt-4 border-t border-rule pt-3">
              <p className="mb-1 text-xs text-muted">Supplier&apos;s bill</p>
              {canManage ? (
                <>
                  <SupplierBillFile
                    value={bill.file ?? null}
                    prefix={`bills/${bill.id}`}
                    disabled={saveFile.isPending}
                    onChange={(f) => saveFile.mutate(f?.fileId ?? null)}
                  />
                  {saveFile.isError ? <p className="mt-1 text-xs font-semibold text-risk">{(saveFile.error instanceof ApiError ? (saveFile.error.body as { error?: string } | null)?.error : null) ?? "Couldn't save the file. Try again."}</p> : null}
                </>
              ) : bill.file?.url ? (
                <a href={bill.file.url} target="_blank" rel="noreferrer" className="text-sm font-semibold underline">{bill.file.filename}</a>
              ) : (
                <p className="text-sm text-muted">None attached</p>
              )}
            </div>
            {bill.notes && (
              <div className="mt-4 border-t border-rule pt-3">
                <p className="text-xs text-muted">Notes</p>
                <p className="mt-1 whitespace-pre-line text-sm text-ink"><NoteText text={bill.notes} /></p>
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-ink">{children}</dd>
    </div>
  );
}

function TotalsRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <dt className={emphasis ? "font-medium text-ink" : "text-muted"}>{label}</dt>
      <dd className={cn("tabular-nums", emphasis ? "font-semibold text-ink" : "text-ink")}>{value}</dd>
    </div>
  );
}
